import { defineNuxtPlugin } from '#app'

import { LicenseAuthorityAdminType } from '@saas/adminTypes'
import headerStore from '@saas/store/header'
import saasLayoutStore from '@saas/store/saasLayout'
import saasApp from '@saas/store/saasApp'
import {
  SaaSPlugin,
  SaaSPremiumPlugin,
  SaaSEnterprisePlugin,
} from '@saas/plugins'

import {
  AdvancedLicenseType,
  AdvancedFreeTrialLicenseType,
  AllSaasUsersLicenseType,
  SaasStaffMembersLicense,
} from '@saas/licenseTypes'
import { registerFreeTrialAwareRoleTypes } from '@saas/roleTypes'

import {
  AdvancedAuditLogPaidFeature,
  ApplicationBuilderSSOPaidFeature,
} from '@saas/paidFeatures'
import {
  CoBrandingPaidFeature,
  SSOPaidFeature,
} from '@baserow_enterprise/paidFeatures'
import { SaaSWorkspaceOnboardingType } from '@saas/onboardingTypes'
import { FreeTrialGuidedTourType } from '@saas/guidedTourTypes'
import { AutomationUsageThresholdNotificationType } from '@saas/notificationTypes.jsx'
import { SaaSFormViewFormModeType } from '@saas/formViewModeTypes'

export default defineNuxtPlugin({
  name: 'saas',
  dependsOn: ['core', 'store', 'i18n', 'premium', 'enterprise', 'license'],
  setup(nuxtApp) {
    const { $store, $registry, $clientErrorMap, $i18n } = nuxtApp
    const context = { app: nuxtApp }

    // Register Vuex store modules
    $store.registerModuleNuxtSafe('header', headerStore)
    $store.registerModuleNuxtSafe('saasLayout', saasLayoutStore)
    $store.registerModuleNuxtSafe('saasApp', saasApp)

    // Register plugins
    $registry.register('plugin', new SaaSPlugin(context))
    $registry.unregister('plugin', 'premium')
    $registry.register('plugin', new SaaSPremiumPlugin(context))
    $registry.register('plugin', new SaaSEnterprisePlugin(context))

    // Register admin types
    $registry.register('admin', new LicenseAuthorityAdminType(context))

    // Register license types
    $registry.register('license', new AllSaasUsersLicenseType(context))
    $registry.register('license', new SaasStaffMembersLicense(context))
    $registry.register('license', new AdvancedLicenseType(context))
    $registry.register('license', new AdvancedFreeTrialLicenseType(context))

    // The billable and free labels must not be shown during the advanced free trial.
    registerFreeTrialAwareRoleTypes($registry, context)

    // Register client error messages
    // When deleting a workspace that has an active subscription
    $clientErrorMap.setError(
      'ERROR_GROUP_HAS_SUBSCRIPTION',
      $i18n.t('clientHandler.workspaceHasSubscriptionTitle'),
      $i18n.t('clientHandler.workspaceHasSubscriptionDescription')
    )

    // When the user tried to delete their account that still has an active subscription
    $clientErrorMap.setError(
      'ERROR_USER_HAS_SUBSCRIPTION',
      $i18n.t('clientHandler.userHasSubscriptionTitle'),
      $i18n.t('clientHandler.userHasSubscriptionDescription')
    )

    $clientErrorMap.setError(
      'ERROR_GROUP_ADMIN_HAS_SUBSCRIPTION',
      $i18n.t('clientHandler.workspaceAdminHasSubscriptionTitle'),
      $i18n.t('clientHandler.workspaceAdminHasSubscriptionDescription')
    )

    $clientErrorMap.setError(
      'ERROR_SUBSCRIPTION_LICENSE_CANT_BE_DELETED',
      $i18n.t('clientHandler.subscriptionLicenseCantBeDeletedTitle'),
      $i18n.t('clientHandler.subscriptionLicenseCantBeDeletedTitleDescription')
    )

    $clientErrorMap.setError(
      'ERROR_ROW_USAGE_LIMIT_EXCEEDED',
      $i18n.t('clientHandler.rowUsageLimitExceededTitle'),
      $i18n.t('clientHandler.rowUsageLimitExceededDescription')
    )

    $clientErrorMap.setError(
      'ERROR_AUTOMATION_USAGE_LIMIT_EXCEEDED',
      $i18n.t('clientHandler.automationUsageLimitExceededTitle'),
      $i18n.t('clientHandler.automationUsageLimitExceededDescription')
    )

    // Register paid features
    $registry.register('paidFeature', new AdvancedAuditLogPaidFeature(context))
    $registry.register(
      'paidFeature',
      new ApplicationBuilderSSOPaidFeature(context)
    )
    $registry.unregister('paidFeature', CoBrandingPaidFeature.getType())
    $registry.unregister('paidFeature', SSOPaidFeature.getType())

    // Register onboarding types
    $registry.register('onboarding', new SaaSWorkspaceOnboardingType(context))

    // Register guided tour types
    $registry.register('guidedTour', new FreeTrialGuidedTourType(context))

    // Register notification types
    $registry.register(
      'notification',
      new AutomationUsageThresholdNotificationType(context)
    )

    // Show a safety warning on forms that look like they're used to scam visitors.
    $registry.register('formViewMode', new SaaSFormViewFormModeType(context))
  },
})
